import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api, type Machine } from '../api/client'
import { invalidateMachinery } from '../api/invalidate'
import { requireAccessToken } from '../auth/msal'
import { useRowSelection } from '../hooks/useRowSelection'
import { useMachineryQueryParams } from '../hooks/useMachineryQueryParams'
import './MachineryRegister.css'

/**
 * Plant and equipment — 562 machines, and deliberately not a second fleet register.
 *
 * There are no compliance cells, no severity strip and no document markers,
 * because the Asset List carries no dates for machinery: no insurance, fitness,
 * permit, tax or PUC column exists for plant anywhere in it. Columns with nothing
 * to put in them would read as "we do not know" rather than "there is nothing to
 * know", and those are different claims.
 *
 * What IS borrowed from the vehicle register is the machinery that earned its
 * keep: filters in the URL, server-side paging and sorting, one round trip for
 * the page and its total, and the same markup collapsing into cards on a phone.
 */
export function MachineryRegister() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [query, setQuery] = useMachineryQueryParams()
  const { selected, toggle, toggleMany, clear } = useRowSelection()

  const open = (machineryId: number) => navigate(`/machinery/${machineryId}`)

  /*
      Straight into edit mode rather than to the record and then a second click.
      A pencil in a row means "I already know what I want to change".
  */
  const edit = (machineryId: number) => navigate(`/machinery/${machineryId}?edit=1`)

  const remove = useMutation({
    mutationFn: async (machine: Machine) => {
      // At the top of the click, while the gesture's user activation is live.
      await requireAccessToken()

      // Re-read for the version rather than carrying one on the grid row. The
      // grid copy is as old as the page, and an import or somebody else editing
      // between then and now would 409 under a message about another person.
      const fresh = await api.getMachine(machine.machineryId)
      return api.deleteMachine(machine.machineryId, fresh.rowVersion!)
    },
    onSuccess: () => invalidateMachinery(queryClient),
  })

  // Debounced into the URL rather than typed straight into it, so a search does
  // not put one history entry per keystroke in the way of the Back button.
  const [searchText, setSearchText] = useState(() => query.search ?? '')

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery((current) => ({ ...current, search: searchText || undefined, page: 1 }))
    }, 250)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  const facets = useQuery({
    queryKey: ['machinery-facets'],
    queryFn: () => api.getMachineryFacets(),
    staleTime: 5 * 60 * 1000,
  })

  const { data, error, isPending } = useQuery({
    queryKey: ['machinery', query],
    queryFn: () => api.getMachinery(query),
    placeholderData: keepPreviousData,
  })

  const rows = useMemo(() => data?.items ?? [], [data])
  const pageIds = useMemo(() => rows.map((r) => r.machineryId), [rows])
  const total = data?.totalRows ?? 0
  const totalPages = data?.totalPages ?? 0
  const page = query.page ?? 1

  const totals = facets.data?.totals

  const filtered =
    query.search ||
    query.machineryCategoryId ||
    query.siteName ||
    query.make ||
    query.hasPlate !== undefined ||
    query.needsAttention !== undefined

  function sortBy(key: string) {
    setQuery((current) => ({
      ...current,
      sortBy: key,
      sortDescending: current.sortBy === key ? !current.sortDescending : false,
      page: 1,
    }))
  }

  return (
    <div className="mach">
      <header className="mach__head">
        <div>
          <p className="mach__eyebrow">Assets</p>
          <h1>Machinery</h1>
          <p className="mach__sub">
            Plant and equipment. No expiry dates are tracked here — the asset list
            carries none for machinery.
          </p>
        </div>

        <div className="mach__right">
          {totals && (
            <div className="mach__stats">
              <Tile
                value={totals.totalMachines}
                label="machines"
                onClick={() => setQuery({ page: 1, sortBy: 'code' })}
              />
              <Tile value={totals.categories} label="categories" />
              <Tile value={totals.sites} label="sites" />
              <Tile
                value={totals.withPlate}
                label="with a plate"
                onClick={() => setQuery((c) => ({ ...c, hasPlate: true, page: 1 }))}
              />
              {/* The only tile that is a to-do rather than a fact, so it is the
                  only one that looks like one. */}
              <Tile
                value={totals.needsAttention}
                label="to clean up"
                tone={totals.needsAttention > 0 ? 'warn' : undefined}
                onClick={() => setQuery((c) => ({ ...c, needsAttention: true, page: 1 }))}
              />
            </div>
          )}

          <div className="mach__actions">
            <button type="button" className="btn" onClick={() => navigate('/machinery/import')}>
              Import asset list
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                /*
                    The filter rides across in the register OWN shape, so the
                    export page reconstructs it with useMachineryQueryParams and
                    there is no second parser to drift from this one. Ticked rows
                    arrive as ?ids=, the way /report takes them.

                    page and pageSize are stripped: paging belongs to the
                    register, not to the export.
                */
                const params = new URLSearchParams(location.search)
                params.delete('page')
                params.delete('pageSize')
                if (selected.size > 0) params.set('ids', [...selected].join(','))
                navigate(`/machinery/export?${params.toString()}`)
              }}
            >
              Export Excel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => navigate('/machinery/new')}
            >
              + Add machine
            </button>
          </div>
        </div>
      </header>

      {error && (
        <p className="mach__error" role="alert">
          {(error as Error).message}
        </p>
      )}

      <div className="mach__controls">
        <label className="mach__field mach__field--search">
          <span>Search</span>
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Code, serial, make, model or plate…"
          />
        </label>

        {/* Every option comes from the data, with its count. A filter that would
            return nothing is a filter nobody should have been offered. */}
        <Facet
          label="Category"
          value={query.machineryCategoryId ? String(query.machineryCategoryId) : ''}
          options={(facets.data?.categories ?? []).map((c) => ({
            value: String(c.id),
            label: `${c.name} (${c.machineCount})`,
          }))}
          onChange={(v) =>
            setQuery((c) => ({ ...c, machineryCategoryId: v ? Number(v) : undefined, page: 1 }))
          }
        />

        <Facet
          label="Site"
          value={query.siteName ?? ''}
          options={(facets.data?.sites ?? []).map((s) => ({
            value: s.name,
            label: `${s.name} (${s.machineCount})`,
          }))}
          onChange={(v) => setQuery((c) => ({ ...c, siteName: v || undefined, page: 1 }))}
        />

        <Facet
          label="Make"
          value={query.make ?? ''}
          options={(facets.data?.makes ?? []).map((m) => ({
            value: m.name,
            label: `${m.name} (${m.machineCount})`,
          }))}
          onChange={(v) => setQuery((c) => ({ ...c, make: v || undefined, page: 1 }))}
        />

        {filtered && (
          <button
            type="button"
            className="mach__clear"
            onClick={() => {
              setSearchText('')
              setQuery({ page: 1, sortBy: 'code' })
            }}
          >
            Clear filters
          </button>
        )}

        <span className="mach__count">
          {isPending ? 'Loading…' : `${total.toLocaleString('en-IN')} shown`}
        </span>
      </div>

      {/* The column headings are the only sort control, and they are hidden in the
          card layout — so the narrow view gets its own select. */}
      <div className="mach__msort">
        <label>
          Sort by
          <select
            value={query.sortBy ?? 'code'}
            onChange={(e) => setQuery((c) => ({ ...c, sortBy: e.target.value, page: 1 }))}
          >
            <option value="code">Asset code</option>
            <option value="category">Category</option>
            <option value="make">Make</option>
            <option value="year">Year</option>
            <option value="site">Site</option>
            <option value="serial">Serial</option>
            <option value="row">Sheet row</option>
          </select>
        </label>
        <button
          type="button"
          className="mach__msort-dir"
          onClick={() => setQuery((c) => ({ ...c, sortDescending: !c.sortDescending }))}
          aria-label={query.sortDescending ? 'Sort ascending' : 'Sort descending'}
        >
          {query.sortDescending ? '↓' : '↑'}
        </button>
      </div>

      {selected.size > 0 && (
        <div className="mach__selbar">
          <span>
            <b>{selected.size}</b> machine{selected.size === 1 ? '' : 's'} ticked
          </span>
          <div>
            <button type="button" className="btn" onClick={clear}>
              Clear
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => navigate(`/machinery/export?ids=${[...selected].join(',')}`)}
            >
              Export these
            </button>
          </div>
        </div>
      )}

      <div className="mach__tablewrap">
        <table className="mach__table">
          {/* table-layout: fixed plus a colgroup. Auto layout sizes every column
              to its widest cell, and one 34-character serial then forces a
              horizontal scrollbar on the whole table. */}
          <colgroup>
            <col className="mach__col-pick" />
            <col className="mach__col-code" />
            <col className="mach__col-cat" />
            <col className="mach__col-make" />
            <col className="mach__col-year" />
            <col className="mach__col-site" />
            <col className="mach__col-flag" />
            <col className="mach__col-act" />
          </colgroup>

          <thead>
            <tr>
              <th scope="col" className="mach__pick">
                <input
                  type="checkbox"
                  checked={pageIds.length > 0 && pageIds.every((id) => selected.has(id))}
                  // indeterminate is a property, not an attribute, so it can only
                  // be set through a ref.
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        pageIds.some((id) => selected.has(id)) &&
                        !pageIds.every((id) => selected.has(id))
                    }
                  }}
                  onChange={() => toggleMany(pageIds)}
                  aria-label="Select every machine on this page"
                />
              </th>
              <SortHeader label="Asset code" sortKey="code" query={query} onSort={sortBy} />
              <SortHeader label="Category" sortKey="category" query={query} onSort={sortBy} />
              <SortHeader label="Make / model" sortKey="make" query={query} onSort={sortBy} />
              <SortHeader label="Year" sortKey="year" query={query} onSort={sortBy} />
              <SortHeader label="Site" sortKey="site" query={query} onSort={sortBy} />
              {/*
                Named, not left blank with only an aria-label.

                An unlabelled column heading is legible to a screen reader and to
                nobody else - a sighted person gets a column of chips under an
                empty box and has to infer what it is. Both of these carry a real
                heading now; "Notes" is what the chips are, and the actions column
                says so rather than relying on two icons to explain themselves.
              */}
              <th scope="col" className="mach__th-plain">Notes</th>
              <th scope="col" className="mach__th-plain mach__th-act">Actions</th>
            </tr>
          </thead>

          <tbody>
            {isPending && (
              <tr>
                <td colSpan={8} className="mach__empty">
                  Loading…
                </td>
              </tr>
            )}

            {!isPending && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="mach__empty">
                  {total === 0 && !filtered ? (
                    <>
                      Nothing here yet. Import the asset list to load the machinery
                      rows from it.
                    </>
                  ) : (
                    <>No machine matches this filter.</>
                  )}
                </td>
              </tr>
            )}

            {rows.map((machine) => (
              <MachineRow
                key={machine.machineryId}
                machine={machine}
                picked={selected.has(machine.machineryId)}
                onPick={() => toggle(machine.machineryId)}
                onOpen={open}
                onEdit={edit}
                onRemove={(m) => remove.mutate(m)}
                removing={
                  remove.isPending && remove.variables?.machineryId === machine.machineryId
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <nav className="mach__pager" aria-label="Pages">
          <button
            type="button"
            className="btn"
            disabled={page <= 1}
            onClick={() => setQuery((c) => ({ ...c, page: page - 1 }))}
          >
            ← Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="btn"
            disabled={page >= totalPages}
            onClick={() => setQuery((c) => ({ ...c, page: page + 1 }))}
          >
            Next →
          </button>
        </nav>
      )}
    </div>
  )
}

function Tile({
  value,
  label,
  tone,
  onClick,
}: {
  value: number
  label: string
  tone?: 'warn'
  onClick?: () => void
}) {
  const className = `mach__tile${tone ? ` mach__tile--${tone}` : ''}`

  if (!onClick) {
    return (
      <div className={className}>
        <b>{value.toLocaleString('en-IN')}</b>
        <span>{label}</span>
      </div>
    )
  }

  return (
    <button type="button" className={className} onClick={onClick}>
      <b>{value.toLocaleString('en-IN')}</b>
      <span>{label}</span>
    </button>
  )
}

function Facet({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}) {
  return (
    // The label sits ABOVE the control. Beside it, three labels of three
    // different widths put three dropdowns at three different x positions on one
    // line, which is what read as misaligned.
    <label className="mach__field mach__facet">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function SortHeader({
  label,
  sortKey,
  query,
  onSort,
}: {
  label: string
  sortKey: string
  query: { sortBy?: string; sortDescending?: boolean }
  onSort: (key: string) => void
}) {
  const active = query.sortBy === sortKey

  return (
    <th scope="col" aria-sort={active ? (query.sortDescending ? 'descending' : 'ascending') : 'none'}>
      <button type="button" onClick={() => onSort(sortKey)}>
        {/* min-width: 0 on the label and flex: none on the caret, or a nowrap
            label pushes the caret past the edge and an ancestor clips it. */}
        <span className="mach__sortlabel">{label}</span>
        <span className="mach__caret" aria-hidden="true">
          {active ? (query.sortDescending ? '↓' : '↑') : ''}
        </span>
      </button>
    </th>
  )
}

function MachineRow({
  machine,
  picked,
  onPick,
  onOpen,
  onEdit,
  onRemove,
  removing,
}: {
  machine: Machine
  picked: boolean
  onPick: () => void
  onOpen: (machineryId: number) => void
  onEdit: (machineryId: number) => void
  onRemove: (machine: Machine) => void
  removing: boolean
}) {
  const [confirming, setConfirming] = useState(false)

  const name = machine.assetCode ?? machine.serialNumber ?? `machine ${machine.machineryId}`

  return (
    // The whole ROW is the target, not just the code. A machine with no asset
    // code had nothing to click at all, and 16 of them are in that state - which
    // read as "machinery has no detail page".
    <tr
      className={picked ? 'mach__picked' : undefined}
      tabIndex={0}
      onClick={() => onOpen(machine.machineryId)}
      onKeyDown={(e) => {
        // Interactive rows must be reachable and operable from the keyboard, not
        // only by pointer.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(machine.machineryId)
        }
      }}
    >
      {/* Its own cell with stopPropagation: the row opens the machine, and
          ticking a box for an export must not open it. */}
      <td className="mach__pick" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={picked}
          onChange={onPick}
          aria-label={`Include ${name} in an export`}
        />
      </td>

      {/* data-label on every cell: the card layout below 820px hides the headings
          and each cell names itself from its own attribute, so a card cannot drift
          out of step with the table. One markup, no second render path. */}
      <td data-label="Asset code">
        <span className="mach__code">
          {machine.assetCode ?? <em>no asset code</em>}
        </span>
        {machine.serialNumber && <span className="mach__serial">{machine.serialNumber}</span>}
      </td>

      <td data-label="Category">{machine.categoryName}</td>

      <td data-label="Make / model">
        {machine.make ?? <span className="mach__dash">&mdash;</span>}
        {machine.model && <span className="mach__model">{machine.model}</span>}
      </td>

      <td data-label="Year" className="mach__num">
        {machine.manufactureYear ?? <span className="mach__dash">&mdash;</span>}
      </td>

      <td data-label="Site">
        {machine.siteNameRaw ?? <span className="mach__dash">&mdash;</span>}
        {/* Shown only where the sheet two site columns disagree, which is 151
            rows. Which one is authoritative is the client call. */}
        {machine.altSiteName && machine.altSiteName !== machine.siteNameRaw && (
          <span className="mach__altsite">also: {machine.altSiteName}</span>
        )}
      </td>

      {/*
        ⚠️ The chips are wrapped in a div, and the wrapper is what flexes.

        `display: flex` on a <td> replaces its `table-cell` display, which takes
        the cell OUT of the table layout - so it stops obeying the colgroup and
        the last column drifts out of line with its heading. Reported as exactly
        that. The cell stays a cell; the box inside it does the layout.
      */}
      <td data-label="Notes">
        <div className="mach__flags">
          {machine.registrationNumberCanonical && (
            <span className="mach__plate" title="Carries an RTO registration">
              {machine.registrationNumberCanonical}
            </span>
          )}
          {machine.needsAttention && (
            <span
              className="mach__warn"
              title="Something that identifies or dates this machine is missing"
            >
              incomplete
            </span>
          )}
        </div>
      </td>

      <td data-label="Actions" onClick={(e) => e.stopPropagation()}>
        <div className="mach__rowacts">
          {confirming ? (
            /*
              Asks before it removes.

              Soft delete, so it is recoverable from the database - but a
              one-click bin on a 562-row grid is how a machine disappears and
              nobody notices for a month. The confirmation is inline rather than
              a modal: it is one row, and a dialog for one row is heavier than
              the decision.
            */
            <>
              <span className="mach__confirm">Remove?</span>
              <button
                type="button"
                className="mach__act mach__act--word mach__act--danger"
                disabled={removing}
                onClick={() => {
                  setConfirming(false)
                  onRemove(machine)
                }}
              >
                {removing ? '…' : 'Yes'}
              </button>
              <button
                type="button"
                className="mach__act mach__act--word"
                onClick={() => setConfirming(false)}
              >
                No
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="mach__act"
                title={`Edit ${name}`}
                aria-label={`Edit ${name}`}
                onClick={() => onEdit(machine.machineryId)}
              >
                <PencilIcon />
              </button>
              <button
                type="button"
                className="mach__act mach__act--danger"
                title={`Remove ${name}`}
                aria-label={`Remove ${name}`}
                onClick={() => setConfirming(true)}
              >
                <BinIcon />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  )
}

function PencilIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M11.2 2.3a1.6 1.6 0 0 1 2.3 2.3l-7.4 7.4-3 .7.7-3 7.4-7.4Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M3 4.5h10M6.5 4.5v-1a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M4.5 4.5l.6 8a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
